import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
  Divider,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import dayjs from "dayjs";
import { getGridButtonSelectorStyles } from "../theme";
import {
  POPPER_CONFIG,
  PAPER_CONFIG,
  SPACING,
} from "../constants/filterSelectors";

const CalendarFilterSelector = ({
  selectedDate = null,
  selectedMonth = null,
  onDateChange,
  onMonthChange,
  viewMode = "Week",
  onViewModeChange,
  statusFilters = ["Requested", "Confirmed", "Completed", "Cancelled"],
  onStatusFiltersChange,
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const triggerBoxShadow =
    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)";
  const textFieldRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Initialize with today's date and current month/year
  const [dayValue, setDayValue] = useState(
    selectedDate ? dayjs(selectedDate) : dayjs(),
  );
  const [monthValue, setMonthValue] = useState(
    selectedMonth ? dayjs(selectedMonth) : dayjs(),
  );

  // Initialize status filters - all checked by default
  const [localStatusFilters, setLocalStatusFilters] = useState(
    statusFilters || ["Requested", "Confirmed", "Completed", "Cancelled"],
  );

  // Track if user actually changed the date or month
  const [dateChanged, setDateChanged] = useState(false);
  const [monthChanged, setMonthChanged] = useState(false);

  // Sync dayValue with selectedDate prop when it changes from parent
  useEffect(() => {
    if (selectedDate) {
      setDayValue(dayjs(selectedDate));
    }
  }, [selectedDate]);

  const handleDayChange = (newValue) => {
    setDateChanged(true);
    if (viewMode === "Day") {
      setDayValue(newValue);
    } else {
      const mondayOfWeek = newValue.startOf("week").add(1, "day");
      setDayValue(mondayOfWeek);
    }
    // Don't fire callback here - only fire on Save
  };

  const handleMonthChange = (newValue) => {
    setMonthChanged(true);
    setDateChanged(true);
    setMonthValue(newValue);
    const mondayOfWeek = newValue.startOf("week").add(1, "day");
    setDayValue(mondayOfWeek);
    // Don't fire callbacks here - only fire on Save
  };

  const handleClick = () => {
    setAnchorEl(anchorEl ? null : textFieldRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getDisplayText = () => {
    const monthText = monthValue.format("YYYY");
    if (viewMode === "Day") {
      return `${dayValue.format("MMM DD")} • ${monthText}`;
    }
    const mondayOfWeek = dayValue.startOf("week").add(1, "day");
    const sundayOfWeek = mondayOfWeek.add(6, "days");
    const weekRangeText = `${mondayOfWeek.format("MMM DD")} - ${sundayOfWeek.format("MMM DD")}`;
    return `${weekRangeText} • ${monthText}`;
  };

  const isDateInCurrentWeek = () => {
    const today = dayjs();
    if (viewMode === "Day") {
      return dayValue.isSame(today, "day");
    }
    const monday = today.startOf("week").add(1, "day"); // Monday of current week
    return dayValue.isSame(monday, "day");
  };

  const handleResetFilter = () => {
    const today = dayjs();
    setDayValue(today);
    setMonthValue(today);
    if (onDateChange) {
      onDateChange(today.toDate());
    }
    if (onMonthChange) {
      onMonthChange(today.toDate());
    }
    setDateChanged(false);
    setMonthChanged(false);
  };

  const handleStatusFilterToggle = (status) => {
    setLocalStatusFilters((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleSave = () => {
    // Only fire date/month callbacks if user actually changed them
    if (dateChanged && onDateChange) {
      if (viewMode === "Day") {
        onDateChange(dayValue ? dayValue.toDate() : null);
      } else {
        const mondayOfWeek = dayValue.startOf("week").add(1, "day");
        onDateChange(mondayOfWeek ? mondayOfWeek.toDate() : null);
      }
    }
    if (monthChanged && onMonthChange) {
      onMonthChange(monthValue ? monthValue.toDate() : null);
    }
    if (onStatusFiltersChange) {
      onStatusFiltersChange(localStatusFilters);
    }
    // Reset change flags
    setDateChanged(false);
    setMonthChanged(false);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const showResetButton = !isDateInCurrentWeek();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{ position: "relative", width: isMobile ? "auto" : "100%" }}
        ref={textFieldRef}
      >
        {isMobile ? (
          <Tooltip title="Calendar Filters">
            <IconButton
              onClick={handleClick}
              size="small"
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.background.default,
                boxShadow: triggerBoxShadow,
                borderRadius: "12px",
                border: "none",
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.buttonHover,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <FilterListRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <TextField
            fullWidth
            size="small"
            label="Calendar"
            value={getDisplayText()}
            onClick={handleClick}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              height: "100%",
              "& .MuiInputBase-root": {
                height: "100%",
                display: "flex",
                alignItems: "center",
                boxShadow: triggerBoxShadow,
                borderRadius: "12px",
                backgroundColor: theme.palette.background.default,
              },
              "& .MuiInputBase-input": {
                py: "8px !important",
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
              },
            }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton
                  edge="end"
                  onClick={handleClick}
                  sx={{ padding: 0.5 }}
                >
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
          />
        )}

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{
            zIndex: POPPER_CONFIG.zIndex,
            width: isMobile ? "calc(100vw - 32px)" : undefined,
          }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={PAPER_CONFIG.elevation}
              sx={{
                p: isMobile ? 1.5 : PAPER_CONFIG.padding,
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.background.default}`,
                borderRadius: PAPER_CONFIG.borderRadius,
                minWidth: isMobile ? "auto" : PAPER_CONFIG.minWidth,
                maxWidth: isMobile ? "calc(100vw - 32px)" : "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: SPACING.containerGap,
                  alignItems: isMobile ? "stretch" : "flex-start",
                }}
              >
                {/* View & Filter - Stacked on Desktop, Side-by-side on Mobile */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "row" : "column",
                    gap: isMobile ? 1 : 0,
                    alignItems: "flex-start",
                    flex: isMobile ? undefined : 1,
                  }}
                >
                  {/* View Options */}
                  <Box sx={{ flex: isMobile ? 1 : undefined }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      View
                    </Typography>
                    <Grid2
                      container
                      spacing={SPACING.gridSpacing}
                      direction="column"
                    >
                      {["Day", "Work Week", "Week"].map((view) => {
                        const isSelected = viewMode === view;
                        return (
                          <Grid2 key={view}>
                            <Box
                              sx={{
                                ...getGridButtonSelectorStyles(
                                  theme,
                                  isSelected,
                                ),
                                py: 0.5,
                                minHeight: "auto",
                              }}
                              onClick={() => {
                                if (onViewModeChange) {
                                  onViewModeChange(view);
                                }
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isSelected
                                    ? "#ffffff"
                                    : theme.palette.primary.main,
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                              >
                                {view}
                              </Typography>
                            </Box>
                          </Grid2>
                        );
                      })}
                    </Grid2>
                  </Box>

                  {/* Divider - Vertical on mobile only when View and Filter are side by side */}
                  {isMobile && (
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  )}

                  {/* Filter Section */}
                  <Box sx={{ flex: isMobile ? 1 : undefined }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      Filter:
                    </Typography>
                    <Grid2
                      container
                      spacing={SPACING.gridSpacing}
                      direction="column"
                    >
                      {["Requested", "Confirmed", "Completed", "Cancelled"].map(
                        (status) => {
                          const isSelected =
                            localStatusFilters.includes(status);
                          return (
                            <Grid2 key={status}>
                              <Box
                                sx={{
                                  ...getGridButtonSelectorStyles(
                                    theme,
                                    isSelected,
                                  ),
                                  py: 0.5,
                                  minHeight: "auto",
                                }}
                                onClick={() => handleStatusFilterToggle(status)}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isSelected
                                      ? "#ffffff"
                                      : theme.palette.primary.main,
                                    fontWeight: isSelected ? "bold" : "normal",
                                  }}
                                >
                                  {status}
                                </Typography>
                              </Box>
                            </Grid2>
                          );
                        },
                      )}
                    </Grid2>
                  </Box>
                </Box>

                {/* Separator - Horizontal on mobile, vertical on desktop */}
                {isMobile && <Divider sx={{ my: 2 }} />}
                {!isMobile && <Divider orientation="vertical" flexItem />}

                {/* Day Calendar */}
                <Box sx={{ pb: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    {viewMode === "Day" ? "Day" : "Week"}
                  </Typography>
                  {(() => {
                    // ===== DAY VIEW BASE STYLES =====
                    // Applies to all days in Day view mode
                    const baseSx = {
                      backgroundColor: theme.palette.background.alt,
                      borderRadius: "12px",
                      "& .MuiDayCalendar-weekContainer": {
                        "& .MuiPickersDay-root": {
                          color: theme.palette.text.primary,
                        },
                      },
                      // ===== TODAY (NOT SELECTED) =====
                      // Styling when viewing today but it's not the selected day
                      "& .MuiPickersDay-today:not([aria-selected='true'])": {
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.alt,
                        border: `2px solid ${theme.palette.primary.main}`,
                        color: theme.palette.text.primary,
                        fontWeight: "bold",
                      },
                    };

                    // ===== WEEK VIEW STYLES =====
                    // Additional styles that only apply in Week/Work Week modes
                    const weekSx = {
                      // ===== TODAY (SELECTED) =====
                      // Styling when today is the selected day in the week
                      "& .MuiDayCalendar-weekContainer:has(> [aria-selected='true']) .MuiPickersDay-today":
                        {
                          borderRadius: "12px",
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.background.alt,
                          fontWeight: "bold",
                        },
                      // ===== OTHER DAYS IN SELECTED WEEK =====
                      // Styling for non-today days that are in the selected week
                      "& .MuiDayCalendar-weekContainer:has(> [aria-selected='true']) .MuiPickersDay-root:not(.MuiPickersDay-today)":
                        {
                          borderRadius: "12px",
                          backgroundColor: theme.palette.primary[200],
                          color: theme.palette.text.primary,
                          fontWeight: "bold",
                        },
                      // ===== DAYS NOT IN SELECTED WEEK =====
                      // Styling for days outside the selected week
                      "& .MuiDayCalendar-weekContainer:not(:has(> [aria-selected='true'])) .MuiPickersDay-root:not(.MuiPickersDay-today)":
                        {
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.alt,
                        },
                    };

                    // ===== DAY VIEW SELECTED STYLES =====
                    // Additional styles for selected day in Day view mode
                    const daySelectedSx = {
                      // ===== SELECTED NON-TODAY DAY =====
                      // Styling for a non-today day when selected in Day view
                      "& .MuiPickersDay-root:not(.MuiPickersDay-today)[aria-selected='true']":
                        {
                          borderRadius: "12px",
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.background.alt,
                          fontWeight: "bold",
                        },
                      // ===== SELECTED TODAY =====
                      // Styling for today when selected in Day view
                      "& .MuiPickersDay-today[aria-selected='true']": {
                        borderRadius: "12px",
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.background.alt,
                        fontWeight: "bold",
                      },
                    };

                    const sx =
                      viewMode === "Day"
                        ? { ...baseSx, ...daySelectedSx }
                        : { ...baseSx, ...weekSx };
                    return (
                      <DateCalendar
                        value={dayValue}
                        onChange={handleDayChange}
                        views={["day", "month"]}
                        sx={{
                          ...sx,

                          py: 0,
                          "& .MuiPickersCalendarHeader-root": {
                            py: 0.5,
                          },
                        }}
                        slotProps={{
                          layout: {
                            sx: {
                              padding: 0,
                            },
                          },
                        }}
                      />
                    );
                  })()}
                </Box>

                {/* Separator - Only show on desktop since Month Calendar is hidden on mobile */}
                {!isMobile && <Divider orientation="vertical" flexItem />}

                {/* Month Calendar */}
                <Box sx={{ pb: 0, display: isMobile ? "none" : "block" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Month & Year
                  </Typography>
                  <DateCalendar
                    defaultValue={dayjs("2022-04-17")}
                    views={["year", "month"]}
                    openTo="month"
                    value={monthValue}
                    onChange={handleMonthChange}
                    sx={{
                      backgroundColor: theme.palette.background.alt,
                      borderRadius: "12px",
                      py: 0,
                      "& .MuiPickersCalendarHeader-root": {
                        py: 0.5,
                      },

                      // ===== SELECTED MONTH =====
                      "& .MuiPickersMonth-monthButton.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.background.alt,
                        fontWeight: "bold",
                        borderRadius: "12px",
                        // Customize selected month appearance above
                      },

                      // ===== NON-SELECTED MONTH =====
                      "& .MuiPickersMonth-monthButton:not(.Mui-selected)": {
                        backgroundColor: theme.palette.background.alt,
                        color: theme.palette.text.primary,
                        borderRadius: "12px",
                        // Add border or other styles here
                      },

                      // ===== SELECTED YEAR =====
                      "& .MuiPickersYear-yearButton.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.background.alt,
                        fontWeight: "bold",
                        borderRadius: "12px",
                        // Customize selected year appearance above
                      },

                      //TO DO: Fix year selection styling

                      // ===== NON-SELECTED YEAR =====
                      "& .MuiPickersYear-yearButton:not(.Mui-selected)": {
                        backgroundColor: theme.palette.background.alt,
                        color: theme.palette.text.primary,
                        borderRadius: "12px",
                        // Add border or other styles here
                      },
                    }}
                    slotProps={{
                      layout: {
                        sx: {
                          padding: 0,
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Divider */}
              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {showResetButton ? (
                  <Button
                    onClick={handleResetFilter}
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<ClearIcon />}
                  >
                    Reset Filter
                  </Button>
                ) : (
                  <Box />
                )}

                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.background.default,
                    borderRadius: "12px",
                    "&:hover": {
                      bgcolor: theme.palette.primary.buttonHoverSelected,
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>
    </LocalizationProvider>
  );
};

export default CalendarFilterSelector;
